import { useEffect, useMemo, useState } from "react";
import Top from "./Top";
import Footer from "./Footer";
import { useSearchParams, useNavigate } from "react-router-dom";

const API_BASE =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/OptionChain/GetOptionChain";

function fmtNum(n) {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n)))
    return "—";
  return Number(n).toLocaleString("en-IN");
}
function fmt2(n) {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n)))
    return "—";
  return Number(n).toFixed(2);
}
function chgClass(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n)))
    return "text-muted";
  return Number(n) >= 0 ? "text-success" : "text-danger";
}

function OptionChain() {
  // UI controls
  const [symbol, setSymbol] = useState("NSE:TCS-EQ");
  const [strikecount, setStrikecount] = useState(1);
  const [search, setSearch] = useState("");
  const [params] = useSearchParams();
  const navigate = useNavigate();

  function isValidSymbol(sym) {
    if (!sym) return false;
    // accept EQ + INDEX formats (edit if you want stricter)
    return /^([A-Z]+):[A-Z0-9]+(-EQ|-INDEX)$/.test(sym);
  }

  useEffect(() => {
    const qSymbol = params.get("symbol");
    const qStrike = params.get("strikecount");

    // ✅ if symbol not present or invalid → go back
    if (!isValidSymbol(qSymbol)) {
      navigate("/sln-scan-live", { replace: true });
      return;
    }

    const sc = qStrike ? Number(qStrike) : 1;

    setSymbol(qSymbol);
    setStrikecount(sc);

    // ✅ fetch using query values
    fetchOptionChain(qSymbol, sc);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  // data state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [raw, setRaw] = useState(null);

  // derived pieces
  const data = raw?.data ?? null;

  const underlying = useMemo(() => {
    if (!data?.optionsChain?.length) return null;
    // FYERS-style chain includes one underlying row with option_type "" and strike_price -1
    return (
      data.optionsChain.find(
        (x) =>
          (!x.option_type || x.option_type === "") &&
          Number(x.strike_price) === -1
      ) || null
    );
  }, [data]);

  const rows = useMemo(() => {
    const list = (data?.optionsChain || []).filter(
      (x) => x.option_type === "CE" || x.option_type === "PE"
    );

    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter((r) => (r.symbol || "").toLowerCase().includes(q));
  }, [data, search]);

  const expiryLabel = useMemo(() => {
    // shows first expiry date (you can add a selector later)
    const first = data?.expiryData?.[0];
    return first?.date ? first.date : "—";
  }, [data]);

  async function fetchOptionChain(symArg, strikeArg) {
    try {
      setErr("");
      setLoading(true);

      const token = localStorage.getItem("SLNToken");
      if (!token) {
        throw new Error("Token not found in localStorage.");
      }

      const sym = symArg ?? symbol;
      const sc = strikeArg ?? strikecount;

      const url = `${API_BASE}?symbol=${encodeURIComponent(
        sym
      )}&strikecount=${encodeURIComponent(sc)}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.s === "error") {
        throw new Error(
          json?.message || json?.error || `Request failed (${res.status})`
        );
      }

      setRaw(json);
    } catch (e) {
      setRaw(null);
      setErr(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  const callOi = data?.callOi;
  const putOi = data?.putOi;
  const pcr =
    callOi && putOi && Number(callOi) !== 0
      ? (Number(putOi) / Number(callOi)).toFixed(2)
      : "—";

  return (
    <>
      <Top />

      <section className="">
        <div className="container mt-4">

          {/* Top Overview */}
          <div className="card border-0 shadow-sm bg-light rounded-4 mb-2">
            <div className="card-body p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="fw-semibold text-dark">Market Overview</div>
                <span className="badge bg-success border border-success-subtle text-white px-3">
                  {loading ? "Loading…" : "Live"}
                </span>
              </div>

              <div className="row g-2">
                {/* Underlying tile */}
                <div className="col-12 col-md-4">
                  <div className="p-4 rounded-4 bg-white border h-100">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="text-muted small">Underlying</div>
                        <div className="fs-5 fw-bold">
                          {underlying?.symbol || symbol}
                        </div>
                        <div className="small">
                          <span
                            className={`fw-semibold ${chgClass(
                              underlying?.ltpch
                            )}`}
                          >
                            {underlying?.ltpch ?? "—"}
                          </span>{" "}
                          <span className={chgClass(underlying?.ltpchp)}>
                            ({underlying?.ltpchp ?? "—"}%)
                          </span>
                        </div>
                      </div>
                      <span
                        className={`badge px-3 ${
                          Number(underlying?.ltpch) >= 0
                            ? "text-bg-success"
                            : "text-bg-danger"
                        }`}
                      >
                        {Number(underlying?.ltpch) >= 0 ? "Bullish" : "Weak"}
                      </span>
                    </div>

                    <hr className="my-3" />

                    <div className="d-flex justify-content-between small text-muted">
                      <span>LTP</span>
                      <span className="text-dark fw-semibold">
                        {fmt2(underlying?.ltp)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between small text-muted">
                      <span>Expiry</span>
                      <span className="text-dark fw-semibold">
                        {expiryLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {/* OI tile */}
                <div className="col-12 col-md-4">
                  <div className="p-4 rounded-4 bg-white border h-100">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="text-muted small">Open Interest</div>
                        <div className="fs-5 fw-bold">PCR: {pcr}</div>
                        <div className="small text-muted">Put OI / Call OI</div>
                      </div>
                      <span className="badge text-bg-dark px-3">OI</span>
                    </div>

                    <hr className="my-3" />

                    <div className="d-flex justify-content-between small text-muted">
                      <span>Call OI</span>
                      <span className="text-dark fw-semibold">
                        {fmtNum(callOi)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between small text-muted">
                      <span>Put OI</span>
                      <span className="text-dark fw-semibold">
                        {fmtNum(putOi)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* VIX tile */}
                <div className="col-12 col-md-4">
                  <div className="p-4 rounded-4 bg-white border h-100">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="text-muted small">India VIX</div>
                        <div className="fs-5 fw-bold">
                          {fmt2(data?.indiavixData?.ltp)}
                        </div>
                        <div className="small">
                          <span
                            className={`fw-semibold ${chgClass(
                              data?.indiavixData?.ltpch
                            )}`}
                          >
                            {fmt2(data?.indiavixData?.ltpch)}
                          </span>{" "}
                          <span
                            className={chgClass(data?.indiavixData?.ltpchp)}
                          >
                            ({fmt2(data?.indiavixData?.ltpchp)}%)
                          </span>
                        </div>
                      </div>
                      <span className="badge text-bg-secondary px-3">
                        Volatility
                      </span>
                    </div>

                    <hr className="my-3" />

                    <div className="d-flex justify-content-between small text-muted">
                      <span>Bid</span>
                      <span className="text-dark fw-semibold">
                        {fmt2(data?.indiavixData?.bid)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between small text-muted">
                      <span>Ask</span>
                      <span className="text-dark fw-semibold">
                        {fmt2(data?.indiavixData?.ask)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expiry chips */}
              <div className="d-flex flex-wrap gap-2 mt-3">
                {(data?.expiryData || []).slice(0, 5).map((e) => (
                  <span
                    key={e.expiry}
                    className="badge rounded-pill text-bg-light border text-dark px-3 py-2"
                  >
                    {e.date}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card border-0 shadow-sm bg-light rounded-4 mb-7">
            <div className="card-body p-3 p-md-4">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3">
                <div>
                  <div className="fw-semibold fs-4 lh-sm text-dark">
                    Live Screener
                  </div>
                  <div className="text-muted fs-5 lh-1">
                    OptionsChain &nbsp;•&nbsp; CE/PE &nbsp;•&nbsp; OI & volume
                  </div>
                </div>

                <div className="d-flex flex-column flex-sm-row align-items-center gap-2 w-100 w-md-auto">
                  <div className="input-group py-2">
                    <span className="input-group-text bg-white">
                      <i className="bi bi-search" />
                    </span>
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      type="text"
                      className="form-control"
                      placeholder="Search symbol… (e.g., 3880CE, 3880PE)"
                    />
                  </div>
                  <span
                    className="bg-dark btn py-2 px-3"
                    onClick={() => setSearch("")}
                    title="Clear"
                  >
                    <i className="bi bi-x-lg text-white" />
                  </span>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr className="text-muted small">
                      <th style={{ minWidth: 240 }}>Symbol</th>
                      <th>LTP</th>
                      <th>CHANGE</th>
                      <th>CHANGE %</th>
                      <th>STRIKE</th>
                      <th>OI</th>
                      <th>OI CHANGE</th>
                      <th>VOLUME</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="py-5 text-center text-muted">
                          Loading option chain…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-5 text-center text-muted">
                          No rows found.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.fyToken || r.symbol}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className={`badge rounded-pill ${
                                  r.option_type === "CE"
                                    ? "text-bg-success"
                                    : "text-bg-warning"
                                }`}
                              >
                                {r.option_type}
                              </span>
                              <div>
                                <div className="fw-semibold">{r.symbol}</div>
                                <div className="text-muted small">
                                  Strike {r.strike_price} • Exp {expiryLabel}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="fw-semibold">{fmt2(r.ltp)}</td>
                          <td className={`fw-semibold ${chgClass(r.ltpch)}`}>
                            {fmt2(r.ltpch)}
                          </td>
                          <td className={chgClass(r.ltpchp)}>
                            {fmt2(r.ltpchp)}%
                          </td>
                          <td>{fmtNum(r.strike_price)}</td>
                          <td>{fmtNum(r.oi)}</td>
                          <td className={`fw-semibold ${chgClass(r.oich)}`}>
                            {fmtNum(r.oich)}
                          </td>
                          <td>{fmtNum(r.volume)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mt-3 gap-2">
                <div className="text-muted fs-5">
                  Showing{" "}
                  <span className="text-dark fw-semibold">{rows.length}</span>{" "}
                  Rows &nbsp;•&nbsp; Update Interval —{" "}
                  <span className="text-dark fw-semibold">
                    Manual / Refresh
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default OptionChain;
